import React, { useState, useEffect } from 'react';
import {
  getZoneVisitHistory,
  getRiskLevelColor,
  getRiskLevelLabel,
  formatDwellTime,
  getZoneCategoryLabel,
} from '../../../services/highRiskZone.service';

/**
 * ZoneVisitHistory - Timeline/table of zone visits
 */
const ZoneVisitHistory = ({ alienId, days = 30, maxItems = 20 }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'table'

  useEffect(() => {
    const fetchHistory = async () => {
      if (!alienId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getZoneVisitHistory(alienId, days);
        setHistory(data);
      } catch (err) {
        console.error('Error fetching zone visit history:', err);
        setError('Failed to load zone visit history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [alienId, days]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Zone Visit History</h3>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-3">
              <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Zone Visit History</h3>
        <div className="text-center py-4 text-red-500 text-sm">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const visits = history?.visits || [];

  if (visits.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Zone Visit History</h3>
        <div className="text-center py-6 text-gray-500 text-sm">
          <div className="text-3xl mb-2">&#x2713;</div>
          <p>No zone visits in the last {days} days</p>
        </div>
      </div>
    );
  }

  const displayVisits = visits.slice(0, maxItems);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Zone Visit History</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{visits.length} visits</span>
          <div className="flex border border-gray-200 rounded overflow-hidden">
            <button
              onClick={() => setViewMode('timeline')}
              className={`px-2 py-1 text-xs ${viewMode === 'timeline' ? 'bg-gray-100 text-gray-700' : 'text-gray-500'
                }`}
            >
              Timeline
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-2 py-1 text-xs ${viewMode === 'table' ? 'bg-gray-100 text-gray-700' : 'text-gray-500'
                }`}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'timeline' ? (
        <TimelineView visits={displayVisits} />
      ) : (
        <TableView visits={displayVisits} />
      )}

      {/* Show more indicator */}
      {visits.length > maxItems && (
        <div className="mt-3 pt-3 border-t border-gray-200 text-center">
          <span className="text-xs text-gray-500">
            Showing {maxItems} of {visits.length} visits
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Timeline view of visits
 */
const TimelineView = ({ visits }) => (
  <div className="relative">
    {/* Timeline line */}
    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

    <div className="space-y-4">
      {visits.map((visit, idx) => (
        <TimelineItem key={visit.id || idx} visit={visit} isLast={idx === visits.length - 1} />
      ))}
    </div>
  </div>
);

/**
 * Single timeline item
 */
const TimelineItem = ({ visit, isLast }) => {
  const riskColor = getRiskLevelColor(visit.riskLevel);
  const entryDate = new Date(visit.entryTime);

  return (
    <div className="relative flex gap-3 pl-8">
      {/* Timeline dot */}
      <div
        className="absolute left-2.5 top-1 w-3 h-3 rounded-full border-2 border-white"
        style={{ backgroundColor: riskColor }}
      />

      {/* Content */}
      <div className={`flex-1 ${!isLast ? 'pb-4' : ''}`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-800">{visit.zoneName}</span>
              <span
                className="text-xs px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: `${riskColor}20`,
                  color: riskColor,
                }}
              >
                Level {visit.riskLevel}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {getZoneCategoryLabel(visit.zoneCategory)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-600">
              {entryDate.toLocaleDateString()}
            </div>
            <div className="text-xs text-gray-400">
              {entryDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>

        {/* Dwell time */}
        {visit.dwellSeconds && (
          <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
            <span>&#x23F1;</span>
            <span>Dwell time: {formatDwellTime(visit.dwellSeconds)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Table view of visits
 */
const TableView = ({ visits }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
          <th className="pb-2 font-medium">Zone</th>
          <th className="pb-2 font-medium">Category</th>
          <th className="pb-2 font-medium text-center">Risk</th>
          <th className="pb-2 font-medium">Entry Time</th>
          <th className="pb-2 font-medium text-right">Dwell</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {visits.map((visit, idx) => (
          <TableRow key={visit.id || idx} visit={visit} />
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Single table row
 */
const TableRow = ({ visit }) => {
  const riskColor = getRiskLevelColor(visit.riskLevel);
  const entryDate = new Date(visit.entryTime);

  return (
    <tr className="hover:bg-gray-50">
      <td className="py-2">
        <span className="font-medium text-gray-800">{visit.zoneName}</span>
      </td>
      <td className="py-2 text-gray-600">
        {getZoneCategoryLabel(visit.zoneCategory)}
      </td>
      <td className="py-2 text-center">
        <span
          className="inline-block w-6 h-6 rounded-full text-xs text-white font-medium leading-6"
          style={{ backgroundColor: riskColor }}
        >
          {visit.riskLevel}
        </span>
      </td>
      <td className="py-2 text-gray-600">
        {entryDate.toLocaleDateString()}{' '}
        <span className="text-gray-400">
          {entryDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </td>
      <td className="py-2 text-right text-gray-600">
        {visit.dwellSeconds ? formatDwellTime(visit.dwellSeconds) : '-'}
      </td>
    </tr>
  );
};

export default ZoneVisitHistory;
